import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNotifications } from "@/hooks/useNotifications";
import { useTabNavigate } from "@/hooks/useTabNavigate";
import { cn } from "@/lib/utils";
import { CheckCircle2 } from "lucide-react";
import { resolveNotificationUrl } from "@/lib/notificationUrl";

const priorityColors: Record<string, string> = {
  urgent: "border-destructive/50 bg-destructive/5",
  high: "border-amber-400/50 bg-amber-50 dark:bg-amber-950/20",
  normal: "border-primary/20",
  low: "border-border",
};

const priorityLabel: Record<string, { icon: string; text: string }> = {
  urgent: { icon: "🔴", text: "OVERDUE" },
  high: { icon: "🟡", text: "TODAY" },
  normal: { icon: "🟡", text: "THIS WEEK" },
  low: { icon: "🔵", text: "WAITING" },
};

export const BallInCourtView = () => {
  const navigate = useTabNavigate();
  const { data: notifications = [], markRead } = useNotifications();

  const ballInCourt = notifications.filter((n) => n.status === "unread");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold">🎯 Ball in Your Court</h2>
          <p className="text-sm text-muted-foreground">
            {ballInCourt.length} item{ballInCourt.length !== 1 ? "s" : ""} need your action
          </p>
        </div>
      </div>

      {ballInCourt.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500 mb-3" />
            <p className="font-medium">All caught up!</p>
            <p className="text-sm text-muted-foreground">Nothing on your plate right now.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {ballInCourt.map((n) => {
            const p = priorityLabel[n.priority] ?? priorityLabel.normal;
            return (
              <Card
                key={n.id}
                className={cn("cursor-pointer transition-colors hover:bg-accent/50", priorityColors[n.priority])}
                onClick={async () => {
                  if (n.status === "unread") {
                    await markRead.mutateAsync(n.id);
                  }
                  const url = resolveNotificationUrl(n);
                  if (url) navigate(url);
                }}
              >
                <CardContent className="flex items-center gap-3 py-3">
                  <span className="text-sm">{p.icon}</span>
                  <Badge variant="outline" className="text-[10px] shrink-0">{p.text}</Badge>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{n.title}</p>
                    {n.entity_type && (
                      <p className="text-[10px] text-muted-foreground capitalize">{n.entity_type.replace(/_/g, " ")}</p>
                    )}
                  </div>
                  {n.action_label && (
                    <Button size="sm" variant="outline">{n.action_label}</Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
